import { useState, useEffect, useRef, useCallback } from 'react';
import { Activity, Database, Zap, Users, MessageSquare, TrendingUp, AlertTriangle, CheckCircle, RefreshCw, Server, HardDrive, Settings, Eye } from 'lucide-react';
import { fetchWithAuth } from '../utils/api';
import { toast } from 'sonner';
import { projectId, API_BASE, API_PUBLIC_BASE } from '../utils/supabase/info';
import { AccessGatePreview } from './AccessGatePreview';
import { useAdminModalZIndex } from './AdminModalZContext';
import { devError, devWarn } from '../utils/logger';


interface HealthStatus {
  status: 'healthy' | 'degraded' | 'error';
  timestamp: string;
  latency: {
    total: number;
    database: number;
  };
  services: {
    database: string;
  };
  errors: {
    database: string | null;
  };
}

interface Metrics {
  activeUsers: number;
  newSignupsToday: number;
  messagesLastHour: number;
  errorRate: number | null;
  avgResponseTime: number | null;
}

interface Resources {
  connections: {
    current: number;
    max: number;
    percentage: number;
  };
  database: {
    sizeGB: number;
    maxGB: number;
    percentage: number;
  };
}

export function AdminHealthDashboard() {
  const adminModalZ = useAdminModalZIndex();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [resources, setResources] = useState<Resources | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  // ✅ NEW: Enhanced error handling states
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState<Date | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('Vedlikeholdsarbeid pågår. Vi er snart tilbake!');
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  // Access gate state
  const [accessGateEnabled, setAccessGateEnabled] = useState(false);
  const [accessGateTitle, setAccessGateTitle] = useState('Registreringen er midlertidig stengt');
  const [accessGateDescription, setAccessGateDescription] = useState('Skriv kort hvorfor du vil bli med, så kontakter vi deg når vi åpner igjen.');
  const [accessGateButtonText, setAccessGateButtonText] = useState('Send forespørsel');
  const [accessGateIcon, setAccessGateIcon] = useState('Lock');
  const [accessGateTheme, setAccessGateTheme] = useState<'default' | 'warning' | 'danger' | 'info' | 'success'>('default');
  const [accessGateAllowClose, setAccessGateAllowClose] = useState(true);
  const [accessGateShowButton, setAccessGateShowButton] = useState(true);
  const [accessGateShowInfo, setAccessGateShowInfo] = useState(true); // ✅ NEW - Toggle for info box
  const [accessGateSubtitle, setAccessGateSubtitle] = useState('Beta-tilgang påkrevd'); // ✅ NEW
  const [accessGateInfoText, setAccessGateInfoText] = useState('<strong>Pilot-fase:</strong> Talentfunn er for øyeblikket i en lukket beta-fase. Hvis du har fått tilgang til pilot-programmet, bruk tilgangskoden du mottok.'); // ✅ NEW
  const [accessGateFooterText, setAccessGateFooterText] = useState('Vil du bli pilot-bruker?'); // ✅ NEW
  const [accessGateContactEmail, setAccessGateContactEmail] = useState('kontakt@talentfunn.no?subject=Pilot-program'); // ✅ NEW
  const [savingAccessGate, setSavingAccessGate] = useState(false);
  const [showFullscreenPreview, setShowFullscreenPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Use ref to track unsaved changes for fetchHealth function
  const hasUnsavedChangesRef = useRef(false);
  const healthFetchInFlightRef = useRef(false);
  
  // Update ref whenever state changes
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const withTimeout = useCallback(async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
    let timeoutId: number | null = null;
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new Error(`${label} timeout`));
          }, timeoutMs);
        }),
      ]);
    } finally {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    }
  }, []);

  // ✅ REFACTORED: Enhanced fetchHealth with timeout guards and overlap protection
  const fetchHealth = useCallback(async (signal?: AbortSignal, isRetry = false) => {
    if (healthFetchInFlightRef.current) return;
    healthFetchInFlightRef.current = true;

    if (!isRetry) {
      setHealthLoading(true);
      setNetworkError(null);
    }

    try {
      // ✅ Create AbortController with 10s timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      // Combine external signal with timeout signal
      const fetchSignal = signal || controller.signal;

      // Public health check (no auth needed)
      const healthRes = await fetch(
        `${API_PUBLIC_BASE}/health`,
        { signal: fetchSignal }
      );
      
      clearTimeout(timeoutId);

      // ✅ Check HTTP status
      if (!healthRes.ok) {
        throw new Error(`Backend returned ${healthRes.status}: ${healthRes.statusText}`);
      }

      const healthData = await healthRes.json();
      
      // Validate health data structure before setting state
      if (healthData && healthData.latency && healthData.services && healthData.errors) {
        setHealth(healthData);
        setLastSuccessfulFetch(new Date());
        setRetryCount(0);
        setNetworkError(null);
      } else {
        devWarn('Incomplete health data received:', healthData);
        // Set a fallback error state
        setHealth({
          status: 'error',
          timestamp: new Date().toISOString(),
          latency: { total: 0, database: 0 },
          services: { database: 'error' },
          errors: { database: 'Invalid response from health endpoint' }
        });
      }

      // Metrics (requires admin auth) - guarded timeout to avoid endless loading
      try {
        const metricsRes = await withTimeout(
          fetchWithAuth(`${API_BASE}/health/metrics`),
          10000,
          'health/metrics'
        );
        const metricsData = await metricsRes.json();
        if (metricsData?.metrics) {
          setMetrics(metricsData.metrics);
        }
        if (metricsData?.resources) {
          setResources(metricsData.resources);
        }
      } catch (metricsError) {
        devWarn('Health metrics fetch failed (continuing with partial data):', metricsError);
      }

      // Fetch system settings (maintenance mode + access gate) with timeout
      try {
        const settingsRes = await withTimeout(
          fetch(`${API_PUBLIC_BASE}/maintenance-mode`, { cache: 'no-store' }),
          10000,
          'maintenance-mode'
        );
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          if (settingsData) {
            setMaintenanceMode(settingsData.maintenanceMode || false);
            setMaintenanceMessage(settingsData.maintenanceMessage || 'Vedlikeholdsarbeid pågår. Vi er snart tilbake!');
            
            if (!hasUnsavedChangesRef.current) {
              setAccessGateEnabled(settingsData.accessGateEnabled || false);
              setAccessGateTitle(settingsData.accessGateTitle || 'Registreringen er midlertidig stengt');
              setAccessGateDescription(settingsData.accessGateDescription || 'Skriv kort hvorfor du vil bli med, så kontakter vi deg når vi åpner igjen.');
              setAccessGateButtonText(settingsData.accessGateButtonText || 'Send forespørsel');
              setAccessGateIcon(settingsData.accessGateIcon || 'Lock');
              setAccessGateTheme(settingsData.accessGateTheme || 'default');
              setAccessGateAllowClose(settingsData.accessGateAllowClose !== undefined ? settingsData.accessGateAllowClose : true);
              setAccessGateShowButton(settingsData.accessGateShowButton !== undefined ? settingsData.accessGateShowButton : true);
              setAccessGateShowInfo(settingsData.accessGateShowInfo !== undefined ? settingsData.accessGateShowInfo : true);
              setAccessGateSubtitle(settingsData.accessGateSubtitle || 'Beta-tilgang påkrevd');
              setAccessGateInfoText(settingsData.accessGateInfoText || '<strong>Pilot-fase:</strong> Talentfunn er for øyeblikket i en lukket beta-fase. Hvis du har fått tilgang til pilot-programmet, bruk tilgangskoden du mottok.');
              setAccessGateFooterText(settingsData.accessGateFooterText || 'Vil du bli pilot-bruker?');
              setAccessGateContactEmail(settingsData.accessGateContactEmail || 'kontakt@talentfunn.no?subject=Pilot-program');
            }
          }
        }
      } catch (settingsError) {
        devWarn('Maintenance settings fetch failed (continuing):', settingsError);
      }

      setLastUpdate(new Date());
      
    } catch (error) {
      // ✅ NEW: Differentiate error types
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          // ✅ AbortError handling
          if (!signal) {
            // Timeout (not external abort)
            devWarn('Health check timeout (10s)');
            setNetworkError('Health check timeout - backend tar for lang tid å svare (>10s)');
            
            // ✅ Retry logic (max 2 retries)
            if (retryCount < 2 && !isRetry) {
              setIsRetrying(true);
              setTimeout(() => {
                setRetryCount(prev => prev + 1);
                fetchHealth(undefined, true).finally(() => setIsRetrying(false));
              }, 2000); // Retry after 2s
              return; // Don't set error state yet
            }
          } else {
            // External abort (component unmount/navigation)
            devWarn('Health check aborted (navigation/unmount) - this is expected');
            // Don't set error state - this is expected behavior
            return;
          }
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('fetch')) {
          // ✅ Network error
          devError('Network error during health check:', error);
          setNetworkError('Nettverksfeil - sjekk internett-tilkobling');
        } else {
          // ✅ Other backend errors
          devError('Backend error during health check:', error);
          setNetworkError(`Backend error: ${error.message}`);
        }
      }

      // Set error state (only if not retrying and not aborted externally)
      if ((retryCount >= 2 || !isRetry) && error.name !== 'AbortError') {
        setHealth({
          status: 'error',
          timestamp: new Date().toISOString(),
          latency: { total: 0, database: 0 },
          services: { database: 'error' },
          errors: { database: networkError || (error instanceof Error ? error.message : 'Unknown error') }
        });
      }
      
    } finally {
      healthFetchInFlightRef.current = false;
      setHealthLoading(false);
      setLoading(false);
    }
  }, [retryCount, hasUnsavedChangesRef, networkError, withTimeout]); // Dependencies for useCallback

  // ✅ REFACTORED: useEffect with AbortController cleanup
  useEffect(() => {
    const controller = new AbortController();
    fetchHealth(controller.signal);

    if (autoRefresh) {
      const interval = setInterval(() => fetchHealth(controller.signal), 30000); // Refresh every 30 seconds
      return () => {
        clearInterval(interval);
        controller.abort(); // Abort ongoing fetch on unmount
      };
    }
  }, [autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return 'text-purple-600';
      case 'degraded':
        return 'text-orange-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return <CheckCircle className="w-5 h-5" />;
      case 'degraded':
      case 'error':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  const getLatencyColor = (latency: number) => {
    if (latency < 100) return 'text-emerald-600';
    if (latency < 300) return 'text-orange-600';
    return 'text-red-600';
  };

  const safePct = (p: number) => (Number.isFinite(p) ? p : 0);

  const handleToggleMaintenance = async () => {
    setSavingMaintenance(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/admin/maintenance-mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: !maintenanceMode,
          message: maintenanceMessage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to toggle maintenance mode');
      }

      setMaintenanceMode(!maintenanceMode);
      toast.success(maintenanceMode ? 'Vedlikeholdsmodus deaktivert' : 'Vedlikeholdsmodus aktivert');
    } catch (error) {
      devError('Failed to toggle maintenance mode:', error);
      toast.error('Kunne ikke endre vedlikeholdsmodus');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const handleSaveAccessGateSettings = async () => {
    setSavingAccessGate(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/admin/access-gate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: accessGateEnabled, // Keep current enabled state
          title: accessGateTitle,
          description: accessGateDescription,
          button_text: accessGateButtonText,
          icon: accessGateIcon,
          theme: accessGateTheme,
          allow_close: accessGateAllowClose,
          show_button: accessGateShowButton,
          show_info: accessGateShowInfo, // ✅ NEW
          subtitle: accessGateSubtitle, // ✅ NEW
          info_text: accessGateInfoText, // ✅ NEW
          footer_text: accessGateFooterText, // ✅ NEW
          contact_email: accessGateContactEmail // ✅ NEW
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save access gate settings');
      }

      toast.success('Innstillinger lagret!');
      setHasUnsavedChanges(false);
    } catch (error) {
      devError('Failed to save access gate settings:', error);
      toast.error('Kunne ikke lagre innstillinger');
    } finally {
      setSavingAccessGate(false);
    }
  };

  const handleToggleAccessGate = async () => {
    setSavingAccessGate(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/admin/access-gate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: !accessGateEnabled,
          title: accessGateTitle,
          description: accessGateDescription,
          button_text: accessGateButtonText,
          icon: accessGateIcon,
          theme: accessGateTheme,
          allow_close: accessGateAllowClose,
          show_button: accessGateShowButton,
          show_info: accessGateShowInfo, // ✅ NEW
          subtitle: accessGateSubtitle, // ✅ NEW
          info_text: accessGateInfoText, // ✅ NEW
          footer_text: accessGateFooterText, // ✅ NEW
          contact_email: accessGateContactEmail // ✅ NEW
        })
      });

      if (!response.ok) {
        throw new Error('Failed to toggle access gate');
      }

      setAccessGateEnabled(!accessGateEnabled);
      toast.success(accessGateEnabled ? 'Adgangsgate deaktivert' : 'Adgangsgate aktivert');
    } catch (error) {
      devError('Failed to toggle access gate:', error);
      toast.error('Kunne ikke endre adgangsgate');
    } finally {
      setSavingAccessGate(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6" />
            System Health
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Sist oppdatert: {lastUpdate.toLocaleTimeString('nb-NO')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded text-purple-600 focus:ring-purple-500"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={() => {
              void fetchHealth();
            }}
            disabled={healthLoading || isRetrying}
            className={`flex items-center gap-2 px-4 py-2 ${
              healthLoading || isRetrying
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
            } text-white rounded-lg transition-colors shadow-sm`}
          >
            <RefreshCw className={`w-4 h-4 ${healthLoading || isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? `Prøver igjen (${retryCount}/2)...` : healthLoading ? 'Sjekker...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Overall Status */}
      {health && health.latency && health.services && (
        <div className="bg-white rounded-xl p-6 border-2 border-purple-100 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={getStatusColor(health.status)}>
                {getStatusIcon(health.status)}
              </div>
              <div>
                <h3 className="text-lg font-semibold capitalize text-gray-900">{health.status}</h3>
                <p className="text-sm text-gray-600">
                  Total responstid: <span className={getLatencyColor(health.latency.total)}>{health.latency.total}ms</span>
                </p>
              </div>
            </div>
            {health.status === 'healthy' && (
              <div className="text-purple-600 text-sm font-medium">
                ✓ Alle systemer operative
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service Status */}
      {health && health.latency && health.services && health.errors && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Database */}
          <div className="bg-white rounded-xl p-6 border-2 border-purple-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Database className={`w-5 h-5 ${getStatusColor(health.services.database)}`} />
                <h3 className="font-semibold text-gray-900">Database</h3>
              </div>
              <span className={`text-sm ${getStatusColor(health.services.database)} capitalize font-medium`}>
                {health.services.database}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Latency:</span>
                <span className={`font-semibold ${getLatencyColor(health.latency.database)}`}>
                  {health.latency.database}ms
                </span>
              </div>
              {health.errors.database && (
                <div className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded border border-red-200">
                  {health.errors.database}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resource Usage */}
      {resources && (
        <div className="bg-white rounded-xl p-6 border-2 border-purple-100 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
            <Server className="w-5 h-5 text-purple-600" />
            Ressursbruk
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Database Connections */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Database Connections</span>
                <span className={`text-sm font-bold ${safePct(resources.connections.percentage) > 70 ? 'text-red-600' : safePct(resources.connections.percentage) > 50 ? 'text-orange-600' : 'text-emerald-600'}`}>
                  {Number.isFinite(resources.connections.percentage) ? `${resources.connections.percentage}%` : '—'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className={`h-2.5 rounded-full ${safePct(resources.connections.percentage) > 70 ? 'bg-red-600' : safePct(resources.connections.percentage) > 50 ? 'bg-orange-600' : 'bg-emerald-600'}`}
                  style={{ width: `${Math.min(100, safePct(resources.connections.percentage))}%` }}
                ></div>
              </div>
              <div className="mt-2 text-xs text-gray-600">
                {resources.connections.current} av {resources.connections.max} connections
              </div>
            </div>

            {/* Database Storage */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Database Storage</span>
                <span className={`text-sm font-bold ${safePct(resources.database.percentage) > 70 ? 'text-red-600' : safePct(resources.database.percentage) > 50 ? 'text-orange-600' : 'text-emerald-600'}`}>
                  {Number.isFinite(resources.database.percentage) ? `${resources.database.percentage}%` : '—'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className={`h-2.5 rounded-full ${safePct(resources.database.percentage) > 70 ? 'bg-red-600' : safePct(resources.database.percentage) > 50 ? 'bg-orange-600' : 'bg-emerald-600'}`}
                  style={{ width: `${Math.min(100, safePct(resources.database.percentage))}%` }}
                ></div>
              </div>
              <div className="mt-2 text-xs text-gray-600">
                {Number.isFinite(resources.database.sizeGB) ? resources.database.sizeGB : '—'} GB av {resources.database.maxGB} GB
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Metrics */}
      {metrics && (
        <div className="bg-white rounded-xl p-6 border-2 border-purple-100 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Sanntid Statistikk
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
              <Users className="w-6 h-6 mx-auto mb-2 text-purple-600" />
              <div className="text-2xl font-bold text-gray-900">{metrics.activeUsers}</div>
              <div className="text-xs text-gray-600 mt-1">Aktive brukere (1t)</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
              <TrendingUp className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
              <div className="text-2xl font-bold text-gray-900">{metrics.newSignupsToday}</div>
              <div className="text-xs text-gray-600 mt-1">Nye i dag</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
              <MessageSquare className="w-6 h-6 mx-auto mb-2 text-pink-600" />
              <div className="text-2xl font-bold text-gray-900">{metrics.messagesLastHour}</div>
              <div className="text-xs text-gray-600 mt-1">Meldinger (1t)</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-orange-600" />
              <div className="text-2xl font-bold text-gray-900">
                {metrics.errorRate != null && Number.isFinite(metrics.errorRate) ? `${metrics.errorRate}%` : '—'}
              </div>
              <div className="text-xs text-gray-600 mt-1">Error rate</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
              <Zap className="w-6 h-6 mx-auto mb-2 text-orange-600" />
              <div className={`text-2xl font-bold ${metrics.avgResponseTime != null && Number.isFinite(metrics.avgResponseTime) ? getLatencyColor(metrics.avgResponseTime) : 'text-gray-500'}`}>
                {metrics.avgResponseTime != null && Number.isFinite(metrics.avgResponseTime) ? `${metrics.avgResponseTime}ms` : '—'}
              </div>
              <div className="text-xs text-gray-600 mt-1">Avg responstid</div>
            </div>
          </div>
        </div>
      )}

      {/* Warnings - IMPROVED with network error detection */}
      {(health?.status !== 'healthy' || networkError) && (
        <div className={`${networkError ? 'bg-yellow-50 border-yellow-200' : 'bg-orange-50 border-orange-200'} border-2 rounded-xl p-6`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 ${networkError ? 'text-yellow-600' : 'text-orange-600'} mt-0.5`} />
            <div className="w-full">
              <h3 className={`font-semibold ${networkError ? 'text-yellow-700' : 'text-orange-700'} mb-2`}>
                {networkError ? 'Nettverksproblem' : 'System Degraded'}
              </h3>
              
              {networkError ? (
                <div>
                  <p className="text-sm text-gray-700 mb-2">{networkError}</p>
                  {lastSuccessfulFetch && (
                    <p className="text-xs text-gray-500 mb-3">
                      Sist vellykket sjekk: {lastSuccessfulFetch.toLocaleTimeString('nb-NO')}
                    </p>
                  )}
                  <div className="mt-3 p-3 bg-white rounded-lg border border-yellow-300">
                    <p className="text-xs text-gray-600 mb-2">
                      💡 <strong>Debugging tips:</strong>
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                      <li>Test backend direkte i browser: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{`${API_PUBLIC_BASE}/health`}</code></li>
                      <li>Hvis backend svarer OK = Dette er kun frontend fetch issue (race condition/timeout)</li>
                      <li>Skru av auto-refresh og refresh manuelt for å unngå race conditions</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    Noen tjenester opplever problemer. Sjekk Supabase Dashboard for CPU/Memory metrics.
                  </p>
                  {health?.errors?.database && (
                    <div className="mt-3">
                      <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
                        <strong>Database error:</strong> {health.errors.database}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Mode */}
      <div className="bg-white rounded-xl p-6 border-2 border-purple-100 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Vedlikeholdsmodus</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Status:</span>
            <span className={`text-sm ${maintenanceMode ? 'text-red-600' : 'text-green-600'} font-medium`}>
              {maintenanceMode ? 'Aktivert' : 'Deaktivert'}
            </span>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-gray-600">Melding:</label>
            <input
              type="text"
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Skriv vedlikeholdsmelding..."
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-gray-600">Handling:</span>
            <button
              onClick={handleToggleMaintenance}
              disabled={savingMaintenance}
              className={`flex items-center gap-2 px-4 py-2 ${savingMaintenance ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'} rounded-lg transition-colors shadow-sm`}
            >
              {savingMaintenance ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {maintenanceMode ? 'Deaktiver' : 'Aktiver'}
            </button>
          </div>
        </div>
      </div>

      {/* Access Gate */}
      <div className="bg-white rounded-xl p-6 border-2 border-purple-100 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">🚧 Adgangsgate</h3>
        <div className="space-y-6">
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <span className="text-sm text-gray-600">Status:</span>
            <span className={`text-sm ${accessGateEnabled ? 'text-red-600' : 'text-green-600'} font-medium`}>
              {accessGateEnabled ? 'Aktivert' : 'Deaktivert'}
            </span>
          </div>
          
          {/* Content Section */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Innhold</h4>
            
            <div className="space-y-2">
              <label className="text-sm text-gray-600">Tittel:</label>
              <input
                type="text"
                value={accessGateTitle}
                onChange={(e) => {
                  setAccessGateTitle(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Skriv tittel..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">Beskrivelse:</label>
              <textarea
                value={accessGateDescription}
                onChange={(e) => {
                  setAccessGateDescription(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="Skriv beskrivelse..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">Knappetekst:</label>
              <input
                type="text"
                value={accessGateButtonText}
                onChange={(e) => {
                  setAccessGateButtonText(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Skriv knappetekst..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">Undertittel (under logo):</label>
              <input
                type="text"
                value={accessGateSubtitle}
                onChange={(e) => {
                  setAccessGateSubtitle(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="F.eks: Beta-tilgang påkrevd"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">Infotekst (i info-boksen):</label>
              <textarea
                value={accessGateInfoText}
                onChange={(e) => {
                  setAccessGateInfoText(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="HTML støttes: <strong>Viktig:</strong> Din melding her..."
                rows={3}
              />
              <p className="text-xs text-gray-500">HTML støttes: &lt;strong&gt;, &lt;em&gt;, osv.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">Bunntekst:</label>
              <input
                type="text"
                value={accessGateFooterText}
                onChange={(e) => {
                  setAccessGateFooterText(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="F.eks: Vil du bli pilot-bruker?"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">Kontakt e-post:</label>
              <input
                type="text"
                value={accessGateContactEmail}
                onChange={(e) => {
                  setAccessGateContactEmail(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="kontakt@talentfunn.no?subject=Pilot-program"
              />
              <p className="text-xs text-gray-500">Kan inkludere ?subject= for forhåndsutfylt emne</p>
            </div>
          </div>

          {/* Appearance Section */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Utseende</h4>
            
            <div className="space-y-2">
              <label className="text-sm text-gray-600">Ikon:</label>
              <select
                value={accessGateIcon}
                onChange={(e) => {
                  setAccessGateIcon(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="Lock">Lock (Standard)</option>
                <option value="AlertCircle">Alert Circle</option>
                <option value="Wrench">Wrench (Vedlikehold)</option>
                <option value="Shield">Shield (Sikkerhet)</option>
                <option value="AlertTriangle">Alert Triangle</option>
                <option value="CheckCircle">Check Circle</option>
                <option value="Info">Info</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">Tema:</label>
              <select
                value={accessGateTheme}
                onChange={(e) => {
                  setAccessGateTheme(e.target.value as any);
                  setHasUnsavedChanges(true);
                }}
                className="w-full text-sm text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="default">Default (Lilla/Rosa)</option>
                <option value="warning">Warning (Oransje/Gul)</option>
                <option value="danger">Danger (Rød)</option>
                <option value="info">Info (Blå)</option>
                <option value="success">Success (Grønn)</option>
              </select>
            </div>
          </div>

          {/* Options Section */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Alternativer</h4>
            
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">Tillat lukking (X-knapp):</label>
              <button
                onClick={() => {
                  setAccessGateAllowClose(!accessGateAllowClose);
                  setHasUnsavedChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  accessGateAllowClose ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    accessGateAllowClose ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">Vis knapp:</label>
              <button
                onClick={() => {
                  setAccessGateShowButton(!accessGateShowButton);
                  setHasUnsavedChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  accessGateShowButton ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    accessGateShowButton ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">Vis info-boks:</label>
              <button
                onClick={() => {
                  setAccessGateShowInfo(!accessGateShowInfo);
                  setHasUnsavedChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  accessGateShowInfo ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    accessGateShowInfo ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Live Preview */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Forhåndsvisning</h4>
            <AccessGatePreview
              title={accessGateTitle}
              description={accessGateDescription}
              buttonText={accessGateButtonText}
              icon={accessGateIcon}
              theme={accessGateTheme}
              allowClose={accessGateAllowClose}
              showButton={accessGateShowButton}
              showInfo={accessGateShowInfo}
              subtitle={accessGateSubtitle}
              infoText={accessGateInfoText}
              footerText={accessGateFooterText}
              contactEmail={accessGateContactEmail}
            />
            <button
              onClick={() => setShowFullscreenPreview(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              Test i fullskjerm
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleSaveAccessGateSettings}
              disabled={savingAccessGate}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 ${savingAccessGate ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'} rounded-lg transition-colors shadow-sm font-medium`}
            >
              {savingAccessGate ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Lagrer...
                </>
              ) : (
                <>
                  <Settings className="w-4 h-4" />
                  Lagre innstillinger
                </>
              )}
            </button>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Aktiver/Deaktiver:</span>
              <button
                onClick={handleToggleAccessGate}
                disabled={savingAccessGate}
                className={`flex items-center gap-2 px-4 py-2 ${savingAccessGate ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : accessGateEnabled ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'} rounded-lg transition-colors shadow-sm`}
              >
                {savingAccessGate ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {accessGateEnabled ? 'Deaktiver' : 'Aktiver'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Preview Modal */}
      {showFullscreenPreview && (
        <AccessGatePreview
          title={accessGateTitle}
          description={accessGateDescription}
          buttonText={accessGateButtonText}
          icon={accessGateIcon}
          theme={accessGateTheme}
          allowClose={accessGateAllowClose}
          showButton={accessGateShowButton}
          showInfo={accessGateShowInfo}
          subtitle={accessGateSubtitle}
          infoText={accessGateInfoText}
          footerText={accessGateFooterText}
          contactEmail={accessGateContactEmail}
          isFullscreen
          forceShowCloseButton={true}
          onClose={() => setShowFullscreenPreview(false)}
          portalZIndex={adminModalZ}
        />
      )}
    </div>
  );
}
